// src/components/UserMeet/User-Meet-Private/BreakoutOverlay.jsx
import React, { useEffect, useRef, useCallback } from "react";
import BreakoutStage from "./BreakoutStage";
import { useSocket } from "@/helpers/useSocket";
import { leaveBreakout } from "@/Services/UserMeetServices";
import { toast } from "sonner";

export default function BreakoutOverlay({
  breakoutData,
  onLeave,
  onEnd,
  onInvite,
  isOwner,
  authToken,
  deviceStatus,
  setDeviceStatus,
  // ✅ NEW: Main meeting connection state passed from UserMeetRoomPage
  mainMeetingConnected,
  mainMeetingEnded,
}) {
  const { socket } = useSocket(authToken);
  const hasAutoLeftRef = useRef(false);

  /* ─── Register in breakout Socket.IO room ─── */
  useEffect(() => {
    if (!socket?.connected || !breakoutData?.breakoutId) return;

    const myUserId = localStorage.getItem("userId") || "";
    const meetingId = breakoutData.meetingId;

    socket.emit("register-usermeet-breakout-user", {
      userId: myUserId,
      meetingId,
      breakoutId: breakoutData.breakoutId,
    });

    console.log(`✅ Registered in breakout room: ${breakoutData.breakoutId}`);

    return () => {
      console.log(`🔌 Unregistering from breakout: ${breakoutData.breakoutId}`);
    };
  }, [socket?.connected, breakoutData?.breakoutId, breakoutData?.meetingId, socket]);

  /* ─── ✅ Auto-leave breakout when main meeting disconnects ─── */
  useEffect(() => {
    // Only trigger if we were connected and now we're not
    if (mainMeetingConnected === false && !hasAutoLeftRef.current) {
      hasAutoLeftRef.current = true;

      console.log("🚪 Main meeting disconnected — auto-leaving breakout...");

      // Silently call the leave API then trigger the onLeave callback
      const autoLeave = async () => {
        if (breakoutData?.breakoutId && breakoutData?.meetingId) {
          try {
            await leaveBreakout(breakoutData.meetingId, breakoutData.breakoutId);
            console.log("✅ Auto-left breakout successfully");
          } catch (err) {
            // Silently ignore — main meeting is already gone
            console.warn("⚠️ Could not call leaveBreakout API (main meeting may already be gone):", err?.message);
          }
        }

        // Always call onLeave to clean up local state
        onLeave?.();
      };

      autoLeave();
    }
  }, [mainMeetingConnected, breakoutData, onLeave]);

  /* ─── ✅ Auto-leave breakout when main meeting is ended by host ─── */
  useEffect(() => {
    if (mainMeetingEnded && !hasAutoLeftRef.current) {
      hasAutoLeftRef.current = true;

      console.log("🛑 Main meeting ended — auto-leaving breakout...");

      // No need to call API since the whole meeting is being torn down
      onLeave?.();
    }
  }, [mainMeetingEnded, onLeave]);

  /* ─── Reset auto-left ref when breakout changes ─── */
  useEffect(() => {
    hasAutoLeftRef.current = false;
  }, [breakoutData?.breakoutId]);

  const handleExpire = useCallback(() => {
    console.log("⏰ Breakout room time expired");
    onLeave?.();
  }, [onLeave]);

  if (!breakoutData) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-5 animate-fadeIn">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-xl" />

      {/* Content Container */}
      <div className="relative w-full max-w-7xl h-[90vh] max-h-[900px] rounded-3xl overflow-hidden shadow-2xl border border-purple-500/20 animate-slideUp">
        <BreakoutStage
          breakoutData={breakoutData}
          deviceStatus={deviceStatus}
          setDeviceStatus={setDeviceStatus}
          onLeave={onLeave}
          onEnd={onEnd}
          onInvite={onInvite}
          isOwner={isOwner}
          onExpire={handleExpire}
        />
      </div>
    </div>
  );
}